import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { naverMapService } from '@/services/naverMap';
import { locationService } from '@/services/location';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import type { LocationCoordinate } from '@/store/location';

export function DesktopMapLocationSelectPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  
  const initialCoordinate = location.state?.coordinate || {
    latitude: 37.5665,
    longitude: 126.9780
  };
  
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [selectedCoordinate, setSelectedCoordinate] = useState<LocationCoordinate>(initialCoordinate);
  const [currentAddress, setCurrentAddress] = useState({
    roadAddress: '위치를 이동하여 주소를 선택하세요',
    jibunAddress: ''
  });
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const fetchAddress = useCallback(async (coordinate: LocationCoordinate) => {
    setIsAddressLoading(true);
    try {
      const address = await locationService.reverseGeocode(coordinate);
      setCurrentAddress(address);
    } catch (error) {
      console.error('주소 변환 실패:', error);
      setCurrentAddress({
        roadAddress: '주소를 가져올 수 없습니다',
        jibunAddress: `위도 ${coordinate.latitude.toFixed(6)}, 경도 ${coordinate.longitude.toFixed(6)}`
      });
    } finally {
      setIsAddressLoading(false);
    }
  }, []);

  const handleMapMove = useCallback(() => {
    if (!mapRef.current) return;
    
    const center = naverMapService.getMapCenter(mapRef.current);
    setSelectedCoordinate(center);
    
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    const timer = setTimeout(() => {
      fetchAddress(center);
    }, 300);
    
    setDebounceTimer(timer);
  }, [debounceTimer, fetchAddress]);

  useEffect(() => {
    const initMap = async () => {
      if (!mapContainerRef.current) return;
      
      try {
        setIsMapLoading(true);
        console.log('지도 초기화 시작...');
        
        await naverMapService.loadMapScript();
        console.log('네이버 지도 스크립트 로드 완료');
        
        const map = await naverMapService.createMap('desktop-map-container', {
          center: initialCoordinate,
          zoom: 16,
          zoomControl: true,
          zoomControlOptions: {
            position: window.naver?.maps?.Position?.TOP_RIGHT
          }
        });
        
        console.log('지도 생성 완료');
        mapRef.current = map;
        
        window.naver.maps.Event.addListener(map, 'idle', handleMapMove);
        
        await fetchAddress(initialCoordinate);
        
      } catch (error) {
        console.error('지도 초기화 실패:', error);
        alert('지도를 불러오는데 실패했습니다. 다시 시도해주세요.');
      } finally {
        setIsMapLoading(false);
      }
    };

    initMap();

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, []);

  const handleSelectAddress = () => {
    if (currentAddress.roadAddress === '주소를 가져올 수 없습니다' || 
        currentAddress.roadAddress === '위치를 이동하여 주소를 선택하세요') {
      alert('유효한 주소를 선택해주세요.');
      return;
    }

    navigate('/address-detail', {
      state: {
        selectedAddress: {
          roadAddress: currentAddress.roadAddress,
          jibunAddress: currentAddress.jibunAddress,
          coordinate: selectedCoordinate
        }
      }
    });
  };

  const handleMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newCoordinate = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          
          if (mapRef.current && window.naver) {
            const latlng = new window.naver.maps.LatLng(newCoordinate.latitude, newCoordinate.longitude);
            mapRef.current.setCenter(latlng);
          }
          
          setSelectedCoordinate(newCoordinate);
          fetchAddress(newCoordinate);
        },
        (error) => {
          console.error('위치 가져오기 실패:', error);
          alert('현재 위치를 가져올 수 없습니다. 위치 권한을 확인해주세요.');
        }
      );
    } else {
      alert('이 브라우저에서는 위치 서비스를 지원하지 않습니다.');
    }
  };

  return (
    <DesktopLayout hasContainer={false}>
      <div className="h-screen flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <img src="/icons/icon_back.svg" alt="뒤로가기" width="28" height="28" />
              </button>
              <h1 className="text-2xl font-bold text-[#222222]">지도에서 위치 선택</h1>
            </div>
            
            <button
              onClick={handleMyLocation}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <img src="/icons/icon_gps.svg" alt="GPS" width="20" height="20" />
              <span className="font-medium">내 위치</span>
            </button>
          </div>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative">
          <div 
            id="desktop-map-container" 
            ref={mapContainerRef}
            className="w-full h-full"
          >
            {isMapLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-3 border-green-500 mx-auto mb-4"></div>
                  <p className="text-lg text-gray-600">지도를 불러오는 중...</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Center Pin */}
          {!isMapLoading && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <div className="w-8 h-8 bg-red-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
          )}
        </div>

        {/* Address Info Panel */}
        <div className="bg-white border-t border-gray-200 p-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-3 gap-6 items-center">
              {/* Address Information */}
              <div className="col-span-2">
                <h3 className="text-lg font-semibold text-[#222222] mb-3">선택된 주소</h3>
                
                {isAddressLoading ? (
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500"></div>
                    <span className="text-gray-600">주소를 가져오는 중...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-600">도로명주소</span>
                      <p className="text-base text-gray-800">{currentAddress.roadAddress}</p>
                    </div>
                    
                    {currentAddress.jibunAddress && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">지번주소</span>
                        <p className="text-base text-gray-800">{currentAddress.jibunAddress}</p>
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 mt-2">
                      위도: {selectedCoordinate.latitude.toFixed(6)}, 
                      경도: {selectedCoordinate.longitude.toFixed(6)}
                    </div>
                  </div>
                )}
              </div>

              {/* Select Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSelectAddress}
                  disabled={isAddressLoading || 
                    currentAddress.roadAddress === '주소를 가져올 수 없습니다' || 
                    currentAddress.roadAddress === '위치를 이동하여 주소를 선택하세요'}
                  className="px-8 py-4 bg-green-500 text-white font-semibold text-lg rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  이 주소로 선택
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DesktopLayout>
  );
}